import type { TFunction } from 'i18next';

import { MaasInferenceSource, MaasRunMode } from '@/delivery-type-plugin/capabilities/maas-init/maasTypes';

import { resolveInferenceSourceFromSystem } from './payload';

/** 推理镜像来源展示文案 */
export function formatMaasInferenceSource(
  source: MaasInferenceSource | undefined,
  t: TFunction,
  inferenceSystem?: Record<string, unknown>,
) {
  const resolved = source ?? resolveInferenceSourceFromSystem(inferenceSystem);
  if (resolved === MaasInferenceSource.Builtin) return t('data-center:maas.inference.source.builtin');
  if (resolved === MaasInferenceSource.Custom) return t('data-center:maas.inference.source.custom');
  return '-';
}

/** 应用镜像来源展示文案 */
export function formatMaasAppImageSource(source: MaasInferenceSource | undefined, t: TFunction, inferenceSystem?: Record<string, unknown>) {
  const resolved = source ?? resolveInferenceSourceFromSystem(inferenceSystem);
  if (resolved === MaasInferenceSource.Builtin) return t('data-center:maas.app.image.source.builtin');
  if (resolved === MaasInferenceSource.Custom) return t('data-center:maas.app.image.source.custom');
  return '-';
}

/** 推理框架展示文案 */
export function formatMaasFramework(framework: string | undefined, t: TFunction) {
  if (!framework) return '-';
  const keyMap: Record<string, string> = {
    vLLM: 'data-center:maas.framework.vllm',
    SGlang: 'data-center:maas.framework.sglang',
  };
  const key = keyMap[framework];
  return key ? t(key) : framework;
}

/** 运行时类型展示文案 */
export function formatMaasRuntimeType(type: string | undefined, t: TFunction) {
  if (!type) return '-';
  return t(`data-center:maas.runtime.${String(type).toLowerCase()}`, type);
}

/** 部署模式 API 值 -> i18n label */
export function formatMaasRunMode(runMode: string | undefined, t: TFunction) {
  if (!runMode) return '-';
  const keyMap: Record<string, string> = {
    Normal: 'data-center:maas.deploy.normal',
    Single: 'data-center:maas.deploy.normal',
    Distributed: 'data-center:maas.deploy.normal',
    PrimarySecondary: 'data-center:maas.deploy.leader.worker',
  };
  const key = keyMap[runMode];
  return key ? t(key) : runMode;
}

/** 将 types API 返回的 runMode 转为 Radio 选项 */
export function buildRunModeOptions(runModes: string[] | undefined, t: TFunction) {
  const modes = runModes || [];
  const hasNormal =
    !modes.length || modes.includes(MaasRunMode.Normal) || modes.includes(MaasRunMode.Single) || modes.includes(MaasRunMode.Distributed);
  const hasPrimarySecondary = !modes.length || modes.includes(MaasRunMode.PrimarySecondary);
  return [
    ...(hasNormal
      ? [
          {
            label: formatMaasRunMode(MaasRunMode.Normal, t),
            value: MaasRunMode.Normal,
          },
        ]
      : []),
    ...(hasPrimarySecondary
      ? [
          {
            label: formatMaasRunMode(MaasRunMode.PrimarySecondary, t),
            value: MaasRunMode.PrimarySecondary,
          },
        ]
      : []),
  ];
}

/** 模型来源 API 值 -> i18n label */
export function formatMaasModelSourceApiLabel(source: string, t: TFunction) {
  const keyMap: Record<string, string> = {
    HTTP: 'data-center:maas.model.source.http',
    S3: 'data-center:maas.model.source.s3',
    ModelScope: 'data-center:maas.model.source.modelscope',
    HuggingFace: 'data-center:maas.model.source.huggingface',
    StackScope: 'hooks:model.repository',
    ModelPlaza: 'home:market.model.repository',
  };
  const key = keyMap[source];
  return key ? t(key) : source;
}
